import React, { ReactNode, useEffect, useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { Box, Flex, Collapse, useDisclosure } from "@chakra-ui/react";
import {
  ButtonPrimary,
  ButtonOutline,
  InputText,
  InputPassword,
} from "@/components/Form";
import { Body, Text } from "@/components/Typography";
import { Link } from "@/components/Navigation";
import { Edit } from "@/components/Icon";
import Alert from "@/components/Alert";
import { useTwoFAStore } from "@/containers/Login/TwoFA/useTwoFAStore";
import { useOTPStore } from "@/containers/Verification/V2/useOTPStore";
import LayoutV2 from "@/layouts/V2";
import { API, Auth, I18n } from "aws-amplify";
import { getIdentityType, userIdenityType } from "@/utility/identityType";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { getPortal } from "@/utility/portal";
import { getDevice, getOS, getPlatform } from "@/utility/userAgent";
import { OAuthFlow } from "@/components/helpers/OAuthFlow";
import { ProfileDetail } from "api-response";
import { getOnboardingToggle } from "@/utility/onboarding";
import { useSetupTwoFAStore } from "@/containers/SetupTwoFA/useSetupTwoFAStore";
import { saveSessionStorage } from "@/utility/saveSessionStorage";
import { OTPActivityVerifiedUser, OTPSessionID, ResetPasswordSessionID } from "@/constants/index";
import { GetPageLink } from "@/utility/getLinkPage";
import { CustomErrors } from "@/constants/customErrorStrings";
import { customErrorMessages } from "@/constants/customErrorMessages";
import Modal from "@/components/Modal";
import ModalHeader from "@/components/Modal/ModalHeader";
import Image from "next/image";
import LoadingPage from "@/components/LoadingPage";
import { Else, If, Then } from "react-if";
import TwoFA from "../TwoFA";
import { checkDeviceLimit } from '@/containers/DeviceLimitPopup/checkDeviceLimit';
import DeviceLimitPopup from '@/containers/DeviceLimitPopup';

const LoginV2 = () => {
  const router = useRouter();
  const [identity, setIdentity] = useState("");
  const [password, setPassword] = useState("");
  const [isButtonSubmitDisabled, setIsButtonSubmitDisabled] = useState(true);
  const [validationIdentityErr, setValidationIdentityErr] = useState("");
  const [alertMsg, setAlertMsg] = useState<string | ReactNode>("");
  const [loadingButton, setLoadingButton] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRequestingOTP, setIsRequestingOTP] = useState(false);
  const setCognitoUser = useOTPStore((s) => s.setCognitoUser);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [sessionData, setSessionData] = useState<any>(null);
  const [isDeviceLimitReached, setIsDeviceLimitReached] = useState(false);
  const [deviceLimitCount, setDeviceLimitCount] = useState(0);

  // TWO FA
  const isOnTwoFA = useTwoFAStore((s) => s.isOnTwoFA);
  const confirmedUser = useTwoFAStore((s) => s.confirmedUser);
  const beginProcessTwoFA = useTwoFAStore((s) => s.beginProcessTwoFA);
  const set2FARedirectionUrl = useSetupTwoFAStore((s) => s.setRedirectionUrl);
  const beginSetupTwoFA = useSetupTwoFAStore((s) => s.beginSetupTwoFA);
  const resetTwoFAState = useTwoFAStore((s) => s.resetTwoFAState);

  // Login username password
  const [user, setUser] = useState<null | any>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { isOpen: isOpenBanned, onClose: onCloseBanned, onOpen: onOpenBanned } = useDisclosure();
  const [typingTimeout, setTypingTimeout] = useState(0);

  // Session validation effect
  useEffect(() => {
    const loginSession = sessionStorage.getItem('login_session');
    if (!loginSession) {
      router.push(GetPageLink('/', router.query));
      return;
    }
    
    try {
      const parsedSession = JSON.parse(loginSession);
      setSessionData(parsedSession);
    } catch (error) {
      console.error('Invalid session data:', error);
      router.push(GetPageLink('/', router.query));
    }

    // Cleanup: Clear session when leaving this page
    return () => {
      sessionStorage.removeItem('login_session');
    };
  }, [router]);

  useEffect(() => {
    if (router.isReady) {
      const username = sessionStorage.getItem('auth_username');
      if (!username) {
        router.push(GetPageLink('/', router.query));
      } else {
        setIdentity(username);
        setIsLoading(false);
      }
    }
  }, [router.isReady, router.query]);

  const resendConfirmationSignUpCode = (userIdentity: string) => {
    const clientMetaData = {
      portal: getPortal(window.location.hostname),
      platform: getPlatform(),
      user_agent: navigator.userAgent,
      device: getDevice(),
      operating_system: getOS(),
      ip_address: "",
    };

    Auth.resendSignUp(userIdentity, clientMetaData)
      .then((user) => {
        saveSessionStorage(OTPSessionID, {
          user_identity: userIdentity,
          activity_otp: OTPActivityVerifiedUser,
        });

        let pathname = GetPageLink("/verification", router.query);

        router.push(pathname);
      })
      .catch((error) => {
        let errMsg = CustomErrors.DEFAULT_MSG;

        if (error.message) {
          if (error.message == CustomErrors.ATTEMPT_LIMIT_EXCEEDED) {
            errMsg = I18n.get("LimitExceededResendCodeException");
          } else {
            // check error message on defined error message lists to prevent showing a technical error message to user
            if (customErrorMessages.hasOwnProperty(error.message)) {
              errMsg = I18n.get(error.message);
            }
          }
        }

        setLoadingButton(false);
        setPassword("");
        setAlertMsg(errMsg);
        // setAlertMsgSocmed("");
      });
  };

  const userForceResetPasswordRequired = (userIdentity: string) => {
    const clientMetaData = {
      portal: getPortal(window.location.hostname),
      platform: getPlatform(),
      user_agent: navigator.userAgent,
      device: getDevice(),
      operating_system: getOS(),
      ip_address: "",
      user_identity: userIdentity,
    };

    Auth.forgotPassword(userIdentity, clientMetaData)
      .then((data) => {
        setLoadingButton(false);

        saveSessionStorage(ResetPasswordSessionID, {
          user_identity: userIdentity,
        });

        let pathname = GetPageLink("/reset-password", router.query);

        // redirect to reset password page for input verification code and new password
        router.push(pathname);
      })
      .catch((error) => {
        let errMsg = CustomErrors.DEFAULT_MSG;

        if (error.message) {
          // check error message on defined error message lists to prevent showing a technical error message to user
          if (customErrorMessages.hasOwnProperty(error.message)) {
            errMsg = I18n.get(error.message);
          }
        }

        setLoadingButton(false);
        setPassword("");
        setAlertMsg(errMsg);
        // setAlertMsgSocmed("");
      });
  };

  const handleLoginWithUsernameAndPassword = async (
    identity: string,
    password: string
  ) => {
    const clientMetaData = {
      portal: getPortal(window.location.hostname),
      platform: getPlatform(),
      user_agent: navigator.userAgent,
      device: getDevice(),
      operating_system: getOS(),
      ip_address: "",
    };

    try {
      const user = await Auth.signIn(identity, password, clientMetaData);
      setUser(user);

      // Handle force change password
      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        // setIsNewPasswordRequiredChallange(true);
        setLoadingButton(false);
        return;
      }

      // Handle 2FA
      if (
        user.challengeName === "SOFTWARE_TOKEN_MFA" ||
        user.challengeName === "SMS_MFA"
      ) {
        await beginProcessTwoFA(user);
        setUser(confirmedUser);
      }

      // Remove session after success login
      sessionStorage.removeItem('otp_session');
      sessionStorage.removeItem('login_session');
      sessionStorage.removeItem('auth_username');

      const deviceLimitResult = await checkDeviceLimit();
      if (deviceLimitResult.blocked) {
        setIsDeviceLimitReached(true);
        setDeviceLimitCount(deviceLimitResult.deviceCount);
        setLoadingButton(false);
        return;
      }

      const OAuthFlowSignIn = await new OAuthFlow().handleSignIn();

      const extraReq = {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getAccessToken()
            .getJwtToken()}`,
        },
      };

      let response: { data?: ProfileDetail } = {};
      try {
        response = (await API.get("profile", "/detail", extraReq)) as {
          data?: ProfileDetail;
        };
      } catch (err) {
        console.error("Gagal ambil data profil:", err);
      }
      const profileDetail = response.data;

      const url = `/api/config/onboarding-toggle`;
      const config = await fetch(url)
        .then((response) => response.json())
        .catch((e) => console.log(e));

      const toggle = getOnboardingToggle(profileDetail, config);

      if (OAuthFlowSignIn?.isOAuthFlow === false) {
        // setIsLoadingPage(false);
        if (
          response?.data?.is_onboarding === false &&
          router.pathname !== "/onboarding" &&
          toggle === "on"
        ) {
          if (
            profileDetail?.is_2fa_enabled === true &&
            profileDetail?.is_2fa_verified === false
          ) {
            set2FARedirectionUrl("/onboarding");
            await beginSetupTwoFA();
          }
          router.push(`/onboarding`);
        } else {
          if (
            profileDetail?.is_2fa_enabled === true &&
            profileDetail?.is_2fa_verified === false
          ) {
            await beginSetupTwoFA();
          }
          setIsLoggedIn(true);
        }
      } else if (OAuthFlowSignIn?.isOAuthFlow === true) {
        // setIsLoadingPage(true);
        if (OAuthFlowSignIn.redirectUrl) {
          if (response?.data?.is_onboarding === false && toggle === "on") {
            const encodedUrl = encodeURIComponent(OAuthFlowSignIn.redirectUrl);
            const redirectUri = `/onboarding?redirect=${encodedUrl}`;

            if (
              profileDetail?.is_2fa_enabled === true &&
              profileDetail?.is_2fa_verified === false
            ) {
              set2FARedirectionUrl(redirectUri);
              await beginSetupTwoFA();
            }

            window.location.replace(redirectUri);
          } else {
            if (
              profileDetail?.is_2fa_enabled === true &&
              profileDetail?.is_2fa_verified === false
            ) {
              set2FARedirectionUrl(OAuthFlowSignIn.redirectUrl);
              await beginSetupTwoFA();
            }

            window.location.replace(OAuthFlowSignIn.redirectUrl);
          }
        }
      }

      setIsLoggedIn(true);
      setLoadingButton(false);
      setPassword("");
    } catch (error: any) {
      if (error.response?.data === "Invalid authorization_code") {
        setIsLoggedIn(true);
      } else if (error.code === "UserNotConfirmedException") {
        resendConfirmationSignUpCode(identity);
      } else if (error.code === "PasswordResetRequiredException") {
        userForceResetPasswordRequired(identity);
      } else {
        let errMsg: string | ReactNode = CustomErrors.DEFAULT_MSG;
        let bannedMsg = error.response?.data?.message || error.message;
        if (bannedMsg?.includes("user already banned")) {
          setLoadingButton(false);
          onOpenBanned();
          return;
        }
        if (error.message) {
          if (
            error.message === CustomErrors.USER_NOT_EXIST ||
            error.message === CustomErrors.USER_NOT_EXIST_IDNACCOUNT
          ) {
            if (getIdentityType(identity) === userIdenityType.PHONE_NUMBER) {
              errMsg = I18n.get("UserNotFoundExceptionPhoneNumber");
            } else {
              errMsg = I18n.get("UserNotFoundExceptionEmail");
            }
          } else if (error.message.includes("kamu melebihi batas device")) {
            errMsg =
              "Akun Anda telah mencapai batas maksimum login. Keluar dari perangkat lain untuk melanjutkan.";
          } else if (customErrorMessages.hasOwnProperty(error.message)) {
            errMsg = I18n.get(error.message);
          } else {
            errMsg = <>{errMsg}<br /><small>{error.message}</small></>
          }
        }

        setLoadingButton(false);
        setAlertMsg(errMsg);
        // setAlertMsgSocmed("");
        setPassword("");
      }
    }
  };

  const handleLoginButtonSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    setLoadingButton(true);
    e.preventDefault();
    setAlertMsg("");

    handleLoginWithUsernameAndPassword(identity, password);
  };

  const handleValidateIdentity = (
    e: React.FormEvent<HTMLInputElement>,
    delay: number = 0
  ) => {
    let userIdentity = (e.target as HTMLInputElement).value
      ?.toLowerCase()
      .trim();
    setIdentity(userIdentity);
    setValidationIdentityErr("");
    setAlertMsg("");
    // setAlertMsgSocmed("");

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    setTypingTimeout(
      window.setTimeout(function () {
        if ((e.target as HTMLInputElement).value == "") {
          setValidationIdentityErr("");
          setIsButtonSubmitDisabled(true);
        } else if (
          getIdentityType(userIdentity) == userIdenityType.PHONE_NUMBER
        ) {
          if (userIdentity.substring(0, 1) == "0") {
            userIdentity = "+62" + userIdentity.substring(1);
          } else if (userIdentity.substring(0, 2) == "62") {
            userIdentity = "+" + userIdentity;
          }
          setIdentity(userIdentity);
        } else {
          setValidationIdentityErr("");
        }
      }, delay)
    );
  };

  const handleValidatePasswordValue = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    setAlertMsg("");
    // setAlertMsgSocmed("");
    setPassword((e.target as HTMLInputElement).value);

    if (validationIdentityErr == "" && identity !== "" && password !== "") {
      setIsButtonSubmitDisabled(false);
    } else {
      setIsButtonSubmitDisabled(true);
    }
  };

  const handleRequestOTP = async () => {
    try {
      setIsRequestingOTP(true);
      setAlertMsg("");

      if (!executeRecaptcha || !identity) return;

      const recaptchaV3Token = await executeRecaptcha('requestOTP_LoginPage');
      const identityType = getIdentityType(identity);
      
      const user = await Auth.signIn(identity);
      const randomCode = Math.floor(100000 + Math.random() * 900000).toString();
      
      const updatedUser = await Auth.sendCustomChallengeAnswer(user, randomCode, {
        currentChallenge: 'LOGIN_TYPE_CHALLENGE',
        recaptchaToken: recaptchaV3Token,
        identityType: identityType
      });

      setCognitoUser(updatedUser);
      
      sessionStorage.setItem('otp_session', JSON.stringify({
        username: identity,
        identityType: identityType,
        is_newUser: false,
        updatedUser: updatedUser,
        uuid: sessionData.uuid,
      }));

      router.push(GetPageLink(`/otp`, router.query))
    } catch (error: any) {
      const errorMessage = error.message || 'Gagal mengirim OTP';
      if (errorMessage.includes("user already banned")) {
        onOpenBanned();
      } else {
        setAlertMsg(errorMessage);
      }
    } finally {
      setIsRequestingOTP(false);
    }
  };

  const handleEditUsername = () => {
    router.push(GetPageLink(`/`, router.query))
  }

  useEffect(() => {
    resetTwoFAState();
  }, []);

  useEffect(() => {
    setIsButtonSubmitDisabled(true);

    if (validationIdentityErr !== "" || identity == "" || password == "") {
      setIsButtonSubmitDisabled(true);
    } else {
      setIsButtonSubmitDisabled(false);
    }
  }, [validationIdentityErr, password, identity]);

  useEffect(() => {
    if (isLoggedIn) router.push(GetPageLink("/", router.query));
  }, [isLoggedIn, router.push, router.asPath]);

  if (isLoading) <LoadingPage />

  return (
    <LayoutV2 backUrl={'/'} withLogo={!isOnTwoFA}>
      <DeviceLimitPopup
        isOpen={isDeviceLimitReached}
        deviceCount={deviceLimitCount}
      />
      <Head>
        <title>Login ke Akun IDN Connect | Masuk dengan Mudah</title>
        <meta
          name="description"
          content="Masuk IDN Connect menggunakan email, nomor telepon, atau media sosial dengan cepat dan aman."
        />
      </Head>
      <If condition={isOnTwoFA}>
        <Then>
          <TwoFA />
        </Then>
        <Else>
          {alertMsg ? <Alert text={alertMsg} /> : null}

          <Flex mb="16px" direction="column" gap="8px">
            <InputText
              data-testid="field-email"
              name="user_identity"
              errorMessage={validationIdentityErr}
              label="Email/No. HP"
              value={identity}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                handleValidateIdentity(e, 0)
              }
              onBlur={(e: React.FormEvent<HTMLInputElement>) =>
                handleValidateIdentity(e)
              }
              backgroundColor="shade06"
              rightIcon={<Edit width={20} height={20} fill="shade02" />}
              onRightIconClick={handleEditUsername}
              isDisabled
              sx={{
                "& ~ label": {
                  top: "12px",
                  color: "shade04",
                  fontWeight: 400,
                  fontSize: "10px",
                  lineHeight: "14px",
                },
              }}
            />

            <Collapse in={showPasswordForm} animateOpacity>
              <form onSubmit={(e) => handleLoginButtonSubmit(e)}>
                <Box>
                  <InputPassword
                    data-testid="field-password"
                    name="password"
                    label="Password"
                    value={password}
                    onChange={(e: React.FormEvent<HTMLInputElement>) =>
                      handleValidatePasswordValue(e)
                    }
                  />
                  <Flex alignItems={"flex-end"} justifyContent={"flex-end"} mt="4px">
                    <Link
                      data-testid="link_forgotPassword"
                      textAlign="right"
                      href="/forgot-password"
                      w={"auto"}
                    >
                      Lupa password
                    </Link>
                  </Flex>
                </Box>
              </form>
            </Collapse>
          </Flex>

          {showPasswordForm ? (
            <>
              <ButtonPrimary
                type="submit"
                data-testid="button-masuk"
                isLoading={loadingButton}
                isDisabled={isButtonSubmitDisabled || isRequestingOTP}
                loadingText="Masuk"
                mb="12px"
                width="100%"
                onClick={(e) => {
                  e.preventDefault();
                  const form = document.querySelector('form');
                  if (form) {
                    form.requestSubmit();
                  }
                }}
              >
                Masuk
              </ButtonPrimary>

              {/* OR Separator */}
              <Box mb="12px">
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  position="relative"
                >
                  <Text
                    variant="othersStatus"
                    backgroundColor="#F6F7F9"
                    color="tarnished_silver"
                    padding="0 16px"
                    position="relative"
                    zIndex="10"
                  >
                    ATAU
                  </Text>
                  <Box
                    position="absolute"
                    border="1px solid #E5E9EC"
                    height="1px"
                    width="100%"
                    left="0"
                    top="50%"
                  />
                </Flex>
              </Box>

              {/* Send OTP Button */}
              <ButtonOutline width="100%" mb="24px" onClick={handleRequestOTP} isLoading={isRequestingOTP}>
                Kirim OTP
              </ButtonOutline>
            </>
          ) : (
            <>
              <ButtonOutline
                width="100%"
                mb="12px"
                onClick={() => setShowPasswordForm(true)}
                isDisabled={isRequestingOTP}
                data-testid="buttonLogin_withPassword"
              >
                Masuk dengan Password
              </ButtonOutline>

              {/* OR Separator */}
              <Box mb="12px">
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  position="relative"
                >
                  <Text
                    variant="othersStatus"
                    backgroundColor="#F6F7F9"
                    color="tarnished_silver"
                    padding="0 16px"
                    position="relative"
                    zIndex="10"
                  >
                    ATAU
                  </Text>
                  <Box
                    position="absolute"
                    border="1px solid #E5E9EC"
                    height="1px"
                    width="100%"
                    left="0"
                    top="50%"
                  />
                </Flex>
              </Box>

              {/* Send OTP Button */}
              <ButtonOutline width="100%" mb="24px" onClick={handleRequestOTP} isLoading={isRequestingOTP} data-testid="buttonLogin_withOTP">
                Kirim OTP
              </ButtonOutline>
            </>
          )}
          <Modal
            onClose={onCloseBanned}
            isOpen={isOpenBanned}
            noMaxHeight={true}
            disabled={true}
            maxWidth={"327px"}
            hasCloseButton={false}
          >
            <ModalHeader showed={false} />
            <Flex alignItems={"center"} justifyContent={"center"} mx={"auto"}>
              <Image
                width={263}
                height={148}
                alt=""
                src="/assets/images/onboarding/account-already-banned.svg"
              />
            </Flex>
            <Body
              variant="Bold16"
              color="shade01"
              display={"flex"}
              alignItems={"center"}
              my={4}
            >
              Akunmu Telah Diblokir
            </Body>
            <Box
              data-testid="modal_successRegistration"
              className="mx-auto flex flex-col"
            >
              <Box mb={6}>
                <Body variant="Default14" color="shade03">
                  Akun ini tidak dapat digunakan lagi karena melakukan pelanggaran:
                </Body>
                <Body
                  variant="Default14"
                  color="shade03"
                  style={{ marginLeft: "1rem" }}
                >
                  <li>Menyiarkan ulang konten IDN Live.</li>
                  <li>Kirim komentar negatif berulang kali.</li>
                  <li>Kirim gambar/chat berisi SARA.</li>
                </Body>
                <Body variant="Default14" color="shade03">
                  Silakan baca penjelasan lengkapnya di bawah ini.
                </Body>
              </Box>
              <Link
                href="/my-account/user-guide#account-banned"
                target="_blank"
                rel="noopener noreferrer"
                style={{ width: "100%" }}
              >
                <ButtonPrimary
                  data-testid="buttonRedirect_PrivacyPolicy"
                  width="100%"
                  mb="8px"
                >
                  Lihat Selengkapnya
                </ButtonPrimary>
              </Link>

              <ButtonOutline
                onClick={onCloseBanned}
                data-testid="buttonClose_accountBanned"
                width="100%"
              >
                Tutup
              </ButtonOutline>
            </Box>
            </Modal>
        </Else>
      </If>
    </LayoutV2>
  );
};

export default LoginV2;
